import { useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { MinimalTiptapEditor } from "@/components/minimal-tiptap";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { BriefSection } from "@/types/types";
import { RocketIcon, X } from "lucide-react";

interface FreeformEditorProps {
  section: BriefSection;
  /** Stored HTML of the whole brief (`brief.brief_content`). */
  content: string;
  onSave: (content: string) => void;
  onClear: () => void;
  /** Same as guided's last section: saves and moves on to the creators tab. */
  onComplete: () => void;
  saving: boolean;
}

/** HTML-aware "is there anything written" — an empty doc still serializes to `<p></p>`. */
const hasText = (html: string) => html.replace(/<[^>]*>/g, "").trim().length > 0;

/**
 * Freeform mode's content page — the whole brief in one rich-text editor.
 * The editor's "frame" variant makes it read like a guided section's Frame
 * (toolbar as the header, document as the panel, actions in the footer),
 * so both modes share one shell. Message sections stay on their own pages.
 */
export function FreeformEditor({
  section,
  content,
  onSave,
  onClear,
  onComplete,
  saving,
}: FreeformEditorProps) {
  // The editor seeds its value once on mount, so after a clear we remount it
  // to show the emptied document.
  const [editorKey, setEditorKey] = useState(0);
  const filled = hasText(content);

  return (
    <div className="max-w-7xl w-full h-full flex flex-col mx-auto">
      <MinimalTiptapEditor
        key={editorKey}
        variant="frame"
        value={content}
        onBlur={(value) => onSave(String(value))}
        output="html"
        placeholder={section.placeholder}
        className="flex-1 min-h-0"
        editorClassName="p-5 min-h-full"
        editorContentClassName="flex-1 min-h-0 overflow-y-auto [scrollbar-color:--alpha(var(--foreground)/20%)_transparent] scrollbar-thin"
        footer={
          <div className="flex items-center justify-end gap-2">
            <span className="mr-auto flex items-center gap-1.5 pl-1.5">
              {saving ? (
                <>
                  <Spinner className="size-3" />
                  Saving…
                </>
              ) : (
                `${section.required ? "Required" : "Optional"} • saves when you click away`
              )}
            </span>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive-ghost"
                  size="sm"
                  disabled={!filled || saving}
                >
                  <X />
                  Clear
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="max-w-lg">
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear the whole brief?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This empties the brief content for this campaign. Your
                    activation and rejection messages are kept.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    variant="destructive"
                    onClick={() => {
                      onClear();
                      setEditorKey((k) => k + 1);
                    }}
                  >
                    <X />
                    Clear brief
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              onClick={onComplete}
              disabled={!filled || saving}
              variant="neutral"
              className="group"
              size="sm"
            >
              Complete
              {saving ? (
                <Spinner />
              ) : (
                <RocketIcon className="group-hover:translate-x-0.25 group-hover:-translate-y-0.25 transition-all" />
              )}
            </Button>
          </div>
        }
      />
    </div>
  );
}
